import React, { useCallback } from 'react';
import { ChevronLeft, CircleSlash, AlertTriangle } from 'lucide-react';
import { Alert, Button, useToast } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { MIRROR_ICON } from '@parsina/framework/clinical/src/i18n/index';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import SegmentedControl from '../../components/common/SegmentedControl';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useMyPageStrings } from './myPageStrings';
import { useMyPageScreen } from './useMyPageScreen';
import { useDoctorPageLeads } from './useDoctorPage';
import { LEADS_PAGE_SIZE, everPublished, orderedSections, errorsBySection } from './MyPage.shared';
import { MyPageMobileSkeleton } from './MyPageSkeleton';
import { StatusCard, TemplatePicker, ThemeCard, SlugField, SectionList, PageBanners } from './parts/rail';
import { FieldStyleContext } from './parts/common';
import PublishDialog from './parts/PublishDialog';
import { LeadCards } from './parts/Leads';
import SectionEditor from './editors/SectionEditor';

// ─────────────────────────────────────────────────────────────────────────────
// «صفحهٔ من» — the phone (MOBILE_WEB_PWA). A reduction of the desktop: one
// column — status, slug, template, theme, the section list — and each section
// opens as a FULL-SCREEN editor (?section=<kind>, so platform back closes it
// and the bottom nav steps aside). Inputs are ≥ 1rem (iOS focus-zoom).
// Render-only: behaviour is useMyPageScreen.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE = { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg };
const SCROLL = { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '1.5rem' };
const MOBILE_FIELD = { fontSize: '1rem' };

export default function MyPageMobile() {
  const s = useMyPageStrings();
  const toast = useToast();
  const navigate = useAppNavigate();
  const notify = useCallback((kind, key) => toast[kind]?.(s(key)), [toast, s]);
  const screen = useMyPageScreen({ notify });
  const { page, loading, error, reload, cards, publishErrors, actions, tab, readOnly } = screen;
  const leads = useDoctorPageLeads({ active: tab === 'leads' && !!page, pageSize: LEADS_PAGE_SIZE, accumulate: true });

  // ── A section's full-screen editor ──
  if (page && tab === 'builder' && screen.section) {
    return (
      <FieldStyleContext.Provider value={MOBILE_FIELD}>
        <div style={PAGE} data-testid="my-page-mobile-editor">
          <div style={{ flexShrink: 0 }}>
            <MobileHeader
              title={s(`myPage.sections.kinds.${screen.section}`)}
              onBack={screen.closeSection}
              backLabel={s('nav.items.myPage')}
              backTestId="my-page-editor-back"
            />
          </div>
          <div style={{ ...SCROLL, padding: '1rem 0.875rem 2rem' }}>
            <SectionEditor
              page={page}
              kind={screen.section}
              actions={actions}
              cards={cards}
              readOnly={readOnly}
              publishErrors={publishErrors}
            />
          </div>
        </div>
      </FieldStyleContext.Provider>
    );
  }

  let body;
  if (loading && !page) {
    body = <MyPageMobileSkeleton />;
  } else if (error && !page) {
    body = (
      <div data-testid="my-page" data-state="error" style={{ padding: '1rem' }}>
        <ErrorState error={error} title={s('myPage.loadFailed')} onRetry={reload} />
      </div>
    );
  } else if (page && tab === 'leads') {
    body = <div style={{ padding: '0 0.875rem' }}><LeadCards leads={leads} published={everPublished(page)} /></div>;
  } else if (page) {
    const errorsByKind = errorsBySection([...(page.publishBlockers || []), ...(publishErrors || [])]);
    const missingCouncil = (page.publishBlockers || []).some((b) => b.code === 'MISSING_COUNCIL_NUMBER');
    body = (
      <div data-testid="my-page" data-state="content" style={{ display: 'grid', gap: '0.75rem', padding: '0 0.875rem' }}>
        <StatusCard
          page={page}
          cards={cards}
          onPreview={screen.preview}
          onPublishClick={screen.openPublish}
          onUnpublish={screen.unpublish}
        />
        {screen.blockedPreview ? (
          <Alert variant="info">
            <a href={screen.blockedPreview} target="_blank" rel="noopener noreferrer" style={{ color: C.navy }}>
              {s('myPage.statusCard.openPreview')}
            </a>
          </Alert>
        ) : null}
        {missingCouncil ? (
          <Button variant="outline" className="min-h-11 w-full" onClick={() => navigate('profile', { tab: 'profile' })} data-testid="my-page-go-profile">
            {s('myPage.statusCard.addCouncil')}
          </Button>
        ) : null}

        {/* The section list — each row opens its full-screen editor. */}
        <section
          aria-label={s('myPage.sections.title')}
          data-testid="my-page-mobile-sections"
          style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '0.5rem', display: 'grid', gap: '0.125rem' }}
        >
          {orderedSections(page.sections).map((sec) => (
            <Button
              key={sec.kind}
              variant="ghost"
              onClick={() => screen.openSection(sec.kind)}
              data-testid={`my-page-mobile-section-${sec.kind}`}
              className="h-auto min-h-11 w-full justify-start gap-3 rounded-xl px-2 py-2 font-normal"
            >
              <span style={{ flex: 1, minWidth: 0, textAlign: 'start', display: 'grid', gap: '0.125rem' }}>
                <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: sec.enabled ? C.textPrimary : C.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                  {s(`myPage.sections.kinds.${sec.kind}`)}
                  {errorsByKind[sec.kind]?.length ? (
                    <AlertTriangle size={14} aria-label={s('myPage.sections.hasErrors')} style={{ color: C.amber }} />
                  ) : null}
                </span>
                <span style={{ fontSize: '0.75rem', color: C.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  {!sec.supportedByTemplate
                    ? (<><CircleSlash size={12} aria-hidden /> {s('myPage.sections.unsupported')}</>)
                    : sec.enabled ? s('myPage.sections.shown') : s('myPage.sections.hidden')}
                </span>
              </span>
              <ChevronLeft size={16} aria-hidden style={{ color: C.textMuted, flexShrink: 0, ...MIRROR_ICON }} />
            </Button>
          ))}
        </section>

        {/* Visibility and order — the same control as the desktop rail. */}
        <SectionList
          page={page}
          cards={cards}
          selected={null}
          onSelect={screen.openSection}
          onToggle={screen.toggleSection}
          onMove={screen.moveSections}
          readOnly={readOnly}
          publishErrors={publishErrors}
          testId="my-page-sections"
        />
        <SlugField page={page} cards={cards} onSave={screen.saveSlug} readOnly={readOnly} />
        <TemplatePicker page={page} cards={cards} onChange={screen.changeTemplate} readOnly={readOnly} />
        <ThemeCard page={page} cards={cards} onChange={screen.changeTheme} readOnly={readOnly} />
      </div>
    );
  }

  return (
    <FieldStyleContext.Provider value={MOBILE_FIELD}>
      <div style={PAGE}>
        <div style={{ flexShrink: 0 }}>
          <MobileHeader title={s('nav.items.myPage')} />
        </div>
        <div style={SCROLL}>
          <div style={{ display: 'grid', gap: '0.75rem', padding: '0.875rem 0.875rem 0' }}>
            {page ? <PageBanners page={page} /> : null}
            <SegmentedControl
              ariaLabel={s('myPage.tabs.label')}
              testId="my-page-tabs"
              value={tab}
              onChange={screen.selectTab}
              items={[
                { id: 'builder', label: s('myPage.tabs.builder') },
                { id: 'leads', label: s('myPage.tabs.leads') },
              ]}
            />
          </div>
          <div style={{ paddingTop: '0.75rem' }}>{body}</div>
        </div>
        {page ? (
          <PublishDialog
            open={screen.publishOpen}
            onClose={screen.closePublish}
            onPublish={screen.publish}
            busy={!!cards.publish?.saving}
            error={cards.publish?.error}
            isMobile
          />
        ) : null}
      </div>
    </FieldStyleContext.Provider>
  );
}
